import type { ListEmailAccountsParams } from '@/lib/email-accounts';
import {
  EMAIL_ACCOUNT_STATUS_VALUES,
  EMAIL_ACCOUNT_PROVIDER_VALUES,
  EMAIL_ACCOUNT_OUTREACH_READINESS_VALUES,
  type EmailAccountsSearch,
  type EmailAccountStatusValue,
  type EmailAccountProviderValue,
  type EmailAccountOutreachReadinessValue,
} from '../search-schema';
import { STATUS_BUCKET_LABEL } from '../account-status';
import { PROVIDER_FILTER_LABELS } from '../active-filters';
import { OUTREACH_READINESS_LABEL } from '../setup-readiness';

// Non-component module (Fast-Refresh-clean): the filter drawer's draft model,
// the URL⇆draft adapters, the control option constants, and the
// search→list-params projection. Lives here (not in the `.tsx`) so the drawer
// + sections files export only React components (see apps/web CLAUDE.md).

/** ESP single-select value. `undefined` = "Any". */
export type EspDraftValue = 'auto' | 'custom' | undefined;

// The per-ESP percentage sub-filter revealed under the ESP section's Custom
// mode. A fixed 3-variant union, not a generic field abstraction — the schema
// has exactly 3 ESP percentage columns.
export const ESP_VOLUME_FIELDS = ['google', 'microsoft', 'other'] as const;
export type EspVolumeField = (typeof ESP_VOLUME_FIELDS)[number];

/** One row of the Custom-mode ESP builder. Both `min`/`max` must be set (and
 * `min <= max`) for the row to be "complete" — see `isEspVolumeFilterValid`. */
export type EspVolumeRow = { esp: EspVolumeField; min?: number; max?: number };

/** Fixed percentage steps for the Min/Max selects. */
export const ESP_VOLUME_STEP_OPTIONS = [10, 20, 30, 40, 50, 60] as const;

/** ESP field → display label. */
export const ESP_VOLUME_FIELD_LABELS: Record<EspVolumeField, string> = {
  google: 'Google',
  microsoft: 'Microsoft',
  other: 'Others',
};

/** Warm-up max sending limit range bounds — mirror the list DTO's
 * `maxLimitMin`/`maxLimitMax` `@Min(5)`/`@Max(100)` (and `search-schema.ts`).
 * At the top bound the filter is open-ended (omitted from the query). */
export const MAX_LIMIT_MIN = 5;
export const MAX_LIMIT_MAX = 100;
export const MAX_LIMIT_STEP = 5;

/** Deliverability-rate range bounds. */
export const DELIVERABILITY_MIN = 0;
export const DELIVERABILITY_MAX = 100;

/** Setup Score range bounds (V1.1) — a distinct filter from Deliverability
 * even though both happen to be 0..100; kept as separate constants so the two
 * controls can diverge independently. */
export const SETUP_SCORE_MIN = 0;
export const SETUP_SCORE_MAX = 100;

/**
 * Account-state multi-select options (#2) — the real, canonical warmup-STATE
 * buckets (Phase 7). Each `EmailAccountStatusValue` is one of the six public
 * `StatusBucket`s (the union excludes the internal `setup` bucket), so the
 * bucket label map keys into it directly.
 */
export const STATE_FILTER_OPTIONS: {
  value: EmailAccountStatusValue;
  label: string;
}[] = EMAIL_ACCOUNT_STATUS_VALUES.map((value) => ({
  value,
  label: STATUS_BUCKET_LABEL[value],
}));

/** Provider multi-select options (#3) — drives the existing `provider[]`. */
export const PROVIDER_OPTIONS: {
  value: EmailAccountProviderValue;
  label: string;
}[] = EMAIL_ACCOUNT_PROVIDER_VALUES.map((value) => ({
  value,
  label: PROVIDER_FILTER_LABELS[value],
}));

/** ESP single-select options (#6). `undefined` = "Any". */
export const ESP_OPTIONS: { value: EspDraftValue; label: string }[] = [
  { value: undefined, label: 'Any' },
  { value: 'auto', label: 'Auto' },
  { value: 'custom', label: 'Custom' },
];

/** Outreach Readiness multi-select options (#1). Label only, no colour dot —
 * the table's own Outreach Readiness cell carries the per-status colour/glyph
 * (see `OUTREACH_READINESS_ICON` in `tanstack/cells-derived.tsx`); this filter
 * list doesn't repeat it, same as `PROVIDER_OPTIONS` above. */
export const OUTREACH_READINESS_OPTIONS: {
  value: EmailAccountOutreachReadinessValue;
  label: string;
}[] = EMAIL_ACCOUNT_OUTREACH_READINESS_VALUES.map((value) => ({
  value,
  label: OUTREACH_READINESS_LABEL[value],
}));

/**
 * The drawer's local draft — a flat, fully-resolved snapshot of every committed
 * filter value. Range fields are always present as `{ min, max }` so the
 * controlled sliders never see `undefined`; `draftToSearchPatch` decides which
 * bounds are "active" (≠ the full track) when projecting back to the URL.
 */
export type FilterDraft = {
  outreachReadiness: EmailAccountOutreachReadinessValue[];
  status: EmailAccountStatusValue[];
  provider: EmailAccountProviderValue[];
  /** May also carry `WITHOUT_TAGS_FILTER_VALUE` — no separate draft field. */
  tags: string[];
  esp: EspDraftValue;
  /** Custom-mode ESP rows. Only meaningful while `esp === 'custom'`. */
  espVolume: EspVolumeRow[];
  domain: string;
  maxLimit: { min: number; max: number };
  deliverability: { min: number; max: number };
  setupScore: { min: number; max: number };
  startDate: { from?: string; to?: string };
  /** Will send today: a sending state plus an enabled slot on today's weekday. */
  scheduledToday: boolean;
};

/** A blank draft — every control at its neutral / full-track state. */
export function emptyFilterDraft(): FilterDraft {
  return {
    outreachReadiness: [],
    status: [],
    provider: [],
    tags: [],
    esp: undefined,
    espVolume: [],
    domain: '',
    maxLimit: { min: MAX_LIMIT_MIN, max: MAX_LIMIT_MAX },
    deliverability: { min: DELIVERABILITY_MIN, max: DELIVERABILITY_MAX },
    setupScore: { min: SETUP_SCORE_MIN, max: SETUP_SCORE_MAX },
    startDate: {},
    scheduledToday: false,
  };
}

/** Reconstruct Custom-mode ESP rows from whichever of the 3 flat
 * `espXxxMin`/`espXxxMax` pairs are present in the URL, in canonical order.
 * A pair counts as "present" if either its min or max is defined. */
function espVolumeFromSearch(search: EmailAccountsSearch): EspVolumeRow[] {
  const rows: EspVolumeRow[] = [];
  if (search.espGoogleMin !== undefined || search.espGoogleMax !== undefined) {
    rows.push({ esp: 'google', min: search.espGoogleMin, max: search.espGoogleMax });
  }
  if (search.espMicrosoftMin !== undefined || search.espMicrosoftMax !== undefined) {
    rows.push({ esp: 'microsoft', min: search.espMicrosoftMin, max: search.espMicrosoftMax });
  }
  if (search.espOthersMin !== undefined || search.espOthersMax !== undefined) {
    rows.push({ esp: 'other', min: search.espOthersMin, max: search.espOthersMax });
  }
  return rows;
}

/** Seed the draft from the current URL search on drawer open. */
export function searchToDraft(search: EmailAccountsSearch): FilterDraft {
  return {
    outreachReadiness: search.outreachReadiness ?? [],
    status: search.status ?? [],
    provider: search.provider ?? [],
    tags: search.tags ?? [],
    esp: search.esp,
    espVolume: espVolumeFromSearch(search),
    domain: search.domain ?? '',
    maxLimit: {
      min: search.maxLimitMin ?? MAX_LIMIT_MIN,
      max: search.maxLimitMax ?? MAX_LIMIT_MAX,
    },
    deliverability: {
      min: search.deliverabilityMin ?? DELIVERABILITY_MIN,
      max: search.deliverabilityMax ?? DELIVERABILITY_MAX,
    },
    setupScore: {
      min: search.setupScoreMin ?? SETUP_SCORE_MIN,
      max: search.setupScoreMax ?? SETUP_SCORE_MAX,
    },
    startDate: { from: search.startDateFrom, to: search.startDateTo },
    scheduledToday: search.scheduledToday === true,
  };
}

/**
 * Project a draft back onto a search patch committed on Apply. Inactive values
 * collapse to `undefined` so they leave NO URL key (mirroring `tags`): a range
 * bound is only emitted when it diverges from the full track, so an untouched
 * slider writes nothing. Every key the drawer owns is present in the patch (as a
 * value or `undefined`) so a cleared filter overwrites a previously-set URL key.
 */
export function draftToSearchPatch(
  draft: FilterDraft,
  /** Min/max fields that have a row in the builder. Each is written with BOTH
   *  bounds exactly as shown — even at its full range — so a row the user
   *  added and applied untouched still filters (and still gets a chip). A
   *  range field with no row falls back to "omit a bound at its limit". */
  rangeRowFields: readonly FilterFieldKey[] = [],
): Partial<EmailAccountsSearch> {
  const domain = draft.domain.trim();
  const shown = (key: RangeFilterFieldKey) => rangeRowFields.includes(key);
  const googleRow = draft.espVolume.find((row) => row.esp === 'google');
  const microsoftRow = draft.espVolume.find((row) => row.esp === 'microsoft');
  const otherRow = draft.espVolume.find((row) => row.esp === 'other');
  return {
    outreachReadiness: draft.outreachReadiness.length > 0 ? draft.outreachReadiness : undefined,
    status: draft.status.length > 0 ? draft.status : undefined,
    provider: draft.provider.length > 0 ? draft.provider : undefined,
    tags: draft.tags.length > 0 ? draft.tags : undefined,
    esp: draft.esp,
    // Each ESP field projects onto its own flat min/max pair, `undefined`
    // when no row exists for that field.
    espGoogleMin: googleRow?.min,
    espGoogleMax: googleRow?.max,
    espMicrosoftMin: microsoftRow?.min,
    espMicrosoftMax: microsoftRow?.max,
    espOthersMin: otherRow?.min,
    espOthersMax: otherRow?.max,
    domain: domain.length > 0 ? domain : undefined,
    maxLimitMin:
      shown('maxLimit') || draft.maxLimit.min > MAX_LIMIT_MIN ? draft.maxLimit.min : undefined,
    maxLimitMax:
      shown('maxLimit') || draft.maxLimit.max < MAX_LIMIT_MAX ? draft.maxLimit.max : undefined,
    deliverabilityMin:
      shown('deliverability') || draft.deliverability.min > DELIVERABILITY_MIN
        ? draft.deliverability.min
        : undefined,
    deliverabilityMax:
      shown('deliverability') || draft.deliverability.max < DELIVERABILITY_MAX
        ? draft.deliverability.max
        : undefined,
    setupScoreMin:
      shown('setupScore') || draft.setupScore.min > SETUP_SCORE_MIN ? draft.setupScore.min : undefined,
    setupScoreMax:
      shown('setupScore') || draft.setupScore.max < SETUP_SCORE_MAX ? draft.setupScore.max : undefined,
    startDateFrom: draft.startDate.from,
    startDateTo: draft.startDate.to,
    scheduledToday: draft.scheduledToday ? true : undefined,
  };
}

/**
 * Project the validated URL search onto the API list params. Shared by the page
 * (`useEmailAccountsListQuery`) and the route loader (`loaderDeps`/`ensureQueryData`)
 * so both produce the identical `ListEmailAccountsParams` — same request, same
 * cache key. Inactive values are already `undefined` on the search object (the
 * Zod schema collapses them), so we pass them through as-is; `listAccounts`
 * omits any `undefined`/empty value from the request.
 */
export function searchToListParams(search: EmailAccountsSearch): ListEmailAccountsParams {
  return {
    page: search.page,
    limit: search.limit,
    status: search.status,
    provider: search.provider,
    q: search.q,
    sort: search.sort,
    dir: search.dir,
    tags: search.tags,
    esp: search.esp,
    maxLimitMin: search.maxLimitMin,
    maxLimitMax: search.maxLimitMax,
    domain: search.domain,
    startDateFrom: search.startDateFrom,
    startDateTo: search.startDateTo,
    deliverabilityMin: search.deliverabilityMin,
    deliverabilityMax: search.deliverabilityMax,
    outreachReadiness: search.outreachReadiness,
    setupScoreMin: search.setupScoreMin,
    setupScoreMax: search.setupScoreMax,
    espGoogleMin: search.espGoogleMin,
    espGoogleMax: search.espGoogleMax,
    espMicrosoftMin: search.espMicrosoftMin,
    espMicrosoftMax: search.espMicrosoftMax,
    espOthersMin: search.espOthersMin,
    espOthersMax: search.espOthersMax,
    scheduledToday: search.scheduledToday,
  };
}

/** Apply-gating for the Custom-mode ESP builder: nothing to validate outside
 * Custom; under Custom, at least one complete row (`min`/`max` set, `min <= max`) is required. */
export function isEspVolumeFilterValid(esp: EspDraftValue, espVolume: EspVolumeRow[]): boolean {
  if (esp !== 'custom') return true;
  if (espVolume.length === 0) return false;
  return espVolume.every(
    (row) => row.min !== undefined && row.max !== undefined && row.min <= row.max,
  );
}

/**
 * The filter builder's fields, in the order the field picker lists them and
 * `countActiveFilterSections` counts them. Each is one `FilterDraft` slice —
 * `esp` also owns `espVolume`, its Custom-mode rows.
 */
export const FILTER_FIELD_KEYS = [
  'outreachReadiness',
  'status',
  'provider',
  'tags',
  'maxLimit',
  'esp',
  'domain',
  'startDate',
  'setupScore',
  'deliverability',
  'scheduledToday',
] as const;
export type FilterFieldKey = (typeof FILTER_FIELD_KEYS)[number];

export const FILTER_FIELD_LABELS: Record<FilterFieldKey, string> = {
  outreachReadiness: 'Outreach readiness',
  status: 'Status',
  provider: 'Provider',
  tags: 'Tags',
  maxLimit: 'Max sending limit',
  esp: 'ESP',
  domain: 'Domain',
  startDate: 'Warm-up start date',
  setupScore: 'Setup score',
  deliverability: 'Deliverability',
  scheduledToday: 'Schedule',
};

/** The one condition each field supports — the list API ANDs every filter and
 * has no negation, so this is copy between the field and its value, not a
 * choice. */
export const FILTER_FIELD_OPERATORS: Record<FilterFieldKey, string> = {
  outreachReadiness: 'is any of',
  status: 'is any of',
  provider: 'is any of',
  tags: 'is any of',
  maxLimit: 'is between',
  esp: 'is',
  domain: 'is',
  startDate: 'is between',
  setupScore: 'is between',
  deliverability: 'is between',
  scheduledToday: 'is',
};

export type RangeFilterFieldKey = 'maxLimit' | 'setupScore' | 'deliverability';

/** Bounds, snap step and unit of the three min/max fields. */
export const RANGE_FILTER_FIELDS: Record<
  RangeFilterFieldKey,
  { min: number; max: number; step: number; suffix: string }
> = {
  maxLimit: { min: MAX_LIMIT_MIN, max: MAX_LIMIT_MAX, step: MAX_LIMIT_STEP, suffix: '' },
  setupScore: { min: SETUP_SCORE_MIN, max: SETUP_SCORE_MAX, step: 1, suffix: '%' },
  deliverability: { min: DELIVERABILITY_MIN, max: DELIVERABILITY_MAX, step: 1, suffix: '%' },
};

/** Whether a field's draft slice holds a value `draftToSearchPatch` would emit. */
export function isFilterFieldActive(draft: FilterDraft, key: FilterFieldKey): boolean {
  switch (key) {
    case 'outreachReadiness':
    case 'status':
    case 'provider':
    case 'tags':
      return draft[key].length > 0;
    case 'esp':
      return draft.esp !== undefined;
    case 'domain':
      return draft.domain.trim().length > 0;
    case 'startDate':
      return draft.startDate.from !== undefined || draft.startDate.to !== undefined;
    case 'scheduledToday':
      return draft.scheduledToday;
    case 'maxLimit':
    case 'setupScore':
    case 'deliverability':
      return (
        draft[key].min > RANGE_FILTER_FIELDS[key].min ||
        draft[key].max < RANGE_FILTER_FIELDS[key].max
      );
  }
}

/** Reset one field's slice to its neutral state — what removing its row does. */
export function clearFilterField(draft: FilterDraft, key: FilterFieldKey): FilterDraft {
  const empty = emptyFilterDraft();
  if (key === 'esp') return { ...draft, esp: empty.esp, espVolume: empty.espVolume };
  return { ...draft, [key]: empty[key] };
}

/**
 * Why the builder's Apply can't be used yet, or `null` when it can. A row that
 * names a field but holds no value blocks it; so does having nothing to apply —
 * unless the URL already carries filters, where applying an emptied builder is
 * how they get cleared. A row still on "Select field" is ignored.
 *
 * A min/max row never blocks: it opens pre-filled with the full range, so it
 * always shows a value, and Apply writes exactly what it shows
 * (`draftToSearchPatch`'s `rangeRowFields`).
 */
export function filterApplyBlockedReason(
  rows: FilterRow[],
  draft: FilterDraft,
  search: EmailAccountsSearch,
): string | null {
  const fields = rows.flatMap((row) => (row.field ? [row.field] : []));
  if (fields.some((field) => !(field in RANGE_FILTER_FIELDS) && !isFilterFieldActive(draft, field))) {
    return 'Choose a value for every filter, or remove the empty one.';
  }
  const applied = searchToDraft(search);
  const hadFilters =
    FILTER_FIELD_KEYS.some((key) => isFilterFieldActive(applied, key)) ||
    rangeFieldsInSearch(search).length > 0;
  if (fields.length === 0 && !hadFilters) return 'Add at least one filter.';
  return null;
}

/** One builder row. `field: null` is a row still on "Select field". */
export type FilterRow = { id: string; field: FilterFieldKey | null };

/**
 * The min/max fields the URL carries a bound for — including a full-range one
 * (say Max sending limit 5–100), which `isFilterFieldActive` reads as inactive
 * because the draft can't tell "at the limit" from "never set".
 */
export function rangeFieldsInSearch(search: EmailAccountsSearch): RangeFilterFieldKey[] {
  const bounds: Record<RangeFilterFieldKey, [number | undefined, number | undefined]> = {
    maxLimit: [search.maxLimitMin, search.maxLimitMax],
    setupScore: [search.setupScoreMin, search.setupScoreMax],
    deliverability: [search.deliverabilityMin, search.deliverabilityMax],
  };
  return (Object.keys(bounds) as RangeFilterFieldKey[]).filter((key) =>
    bounds[key].some((bound) => bound !== undefined),
  );
}

/**
 * The rows a draft opens with: one per active field, or a single blank row.
 * Pass the URL `search` so a full-range min/max filter reopens as its row.
 */
export function seedFilterRows(draft: FilterDraft, search?: EmailAccountsSearch): FilterRow[] {
  const inSearch: readonly FilterFieldKey[] = search ? rangeFieldsInSearch(search) : [];
  const active = FILTER_FIELD_KEYS.filter(
    (key) => isFilterFieldActive(draft, key) || inSearch.includes(key),
  );
  if (active.length === 0) return [{ id: crypto.randomUUID(), field: null }];
  return active.map((field) => ({ id: crypto.randomUUID(), field }));
}
